import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const sections = [
    { id: 'introduction', title: '1. Introduction' },
    { id: 'data-collected', title: '2. Types of Data Collected' },
    { id: 'purpose', title: '3. Purpose of Data Collection' },
    { id: 'children', title: '4. Processing of Children\'s Data' },
    { id: 'legal-basis', title: '5. Legal Basis (DPDP Act 2023)' },
    { id: 'storage', title: '6. Data Storage & Protection' },
    { id: 'sharing', title: '7. Data Sharing & Third-Party Disclosures' },
    { id: 'video-media', title: '8. Video & Media Data Handling' },
    { id: 'user-rights', title: '9. User Rights Under DPDP Act' },
    { id: 'retention', title: '10. Data Retention Policy' },
    { id: 'cookies', title: '11. Cookies & Tracking' },
    { id: 'transfers', title: '12. Data Transfers' },
    { id: 'security', title: '13. Security Measures' },
    { id: 'grievance', title: '14. Grievance Redressal Mechanism' },
    { id: 'updates', title: '15. Policy Updates' },
    { id: 'contact', title: '16. Contact Information' },
  ];

  return (
    <div className="bg-background min-h-[calc(100vh-64px)] py-10">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-card p-8 rounded-sm border border-border">
          <h1 className="text-3xl font-bold mb-4 text-foreground">Privacy Policy</h1>
          <p className="text-muted-foreground mb-2">Last Updated: December 3, 2025</p>
          <p className="text-muted-foreground mb-8">Version 2.0</p>

          {/* Table of Contents */}
          <nav className="mb-10 p-6 bg-muted/30 rounded-lg border border-border">
            <h2 className="text-xl font-semibold mb-4 text-foreground">Table of Contents</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {sections.map((section) => (
                <li key={section.id}>
                  <a 
                    href={`#${section.id}`} 
                    className="text-secondary hover:text-secondary/80 hover:underline text-sm"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Section 1: Introduction */}
          <section id="introduction" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">1. Introduction</h2>
            <p className="mb-4 text-muted-foreground">
              Testera ("we", "our", "us", or the "Company") is committed to protecting your privacy and ensuring the security of your personal data. This Privacy Policy explains how we collect, use, disclose, store, and safeguard your information when you use our AI-powered job assessment and educational testing platform.
            </p>
            <p className="mb-4 text-muted-foreground">
              <strong>Scope:</strong> This policy applies to all users of the Testera website (testera.ai), mobile applications, and our assessment platform, including candidates, employers, educational institutions, students, and administrators.
            </p>
            <p className="mb-4 text-muted-foreground">
              <strong>Legal Framework:</strong> This Privacy Policy is drafted in compliance with the Digital Personal Data Protection Act, 2023 (DPDP Act) of India, and incorporates principles of data protection recognized globally.
            </p>
            <p className="text-muted-foreground">
              By accessing or using Testera's services, you acknowledge that you have read, understood, and consent to the collection and use of your information in accordance with this policy.
            </p>
          </section>

          {/* Section 2: Types of Data Collected */}
          <section id="data-collected" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">2. Types of Data Collected</h2>
            
            <h3 className="text-xl font-medium mb-3 text-foreground">2.1 Personal Information</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number (if provided)</li>
              <li>Student ID or employee ID (if applicable)</li>
              <li>Profile photograph (if uploaded)</li>
              <li>Educational or professional background</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">2.2 Account & Login Data</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Account credentials (encrypted)</li>
              <li>Authentication tokens</li>
              <li>Login timestamps and session data</li>
              <li>Password reset requests</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">2.3 Assessment Data</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Test responses and submissions</li>
              <li>Assessment scores and results</li>
              <li>Time logs (start time, end time, time per question)</li>
              <li>Question-level performance data</li>
              <li>AI-generated feedback and evaluations</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">2.4 Video & Audio Recordings</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Video recordings from webcam during assessments</li>
              <li>Audio recordings for spoken response questions</li>
              <li>Webcam snapshots for proctoring and integrity verification</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">2.5 Device & Technical Information</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Device type and identifiers</li>
              <li>Screen resolution</li>
              <li>Network information</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">2.6 Usage Logs & Monitoring Events</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Tab switching and window focus events</li>
              <li>Clipboard actions</li>
              <li>Navigation and page view data</li>
              <li>Error logs and crash reports</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">2.7 Cookies & Tracking Data</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Session cookies for authentication</li>
              <li>Preference cookies</li>
              <li>Analytics data (anonymized)</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">2.8 Institution-Provided Data</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Student or candidate lists provided by schools, colleges, or employers</li>
              <li>Class or department information</li>
              <li>Test assignment data</li>
            </ul>
          </section>

          {/* Section 3: Purpose of Data Collection */}
          <section id="purpose" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">3. Purpose of Data Collection</h2>
            <p className="mb-4 text-muted-foreground">We collect and process your data for the following purposes:</p>
            
            <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
              <li><strong>Account Creation & Authentication:</strong> To create and manage your account, verify your identity, and provide secure access to our platform.</li>
              <li><strong>Conducting Assessments:</strong> To deliver tests, record responses, and enable you to complete assessments.</li>
              <li><strong>Evaluation & Scoring:</strong> To evaluate your responses using AI-powered tools and generate scores, feedback, and analytics.</li>
              <li><strong>Integrity & Security:</strong> To detect and prevent fraud, cheating, or unauthorized access during assessments.</li>
              <li><strong>Technical Support:</strong> To troubleshoot issues, resolve errors, and improve platform performance.</li>
              <li><strong>Communication:</strong> To send you test invitations, reminders, results, and important platform updates.</li>
              <li><strong>Analytics & Improvement:</strong> To analyze usage patterns and improve our services, user experience, and assessment quality.</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal requests.</li>
            </ul>
          </section>

          {/* Section 4: Processing of Children's Data */}
          <section id="children" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">4. Processing of Children's Data</h2>
            <p className="mb-4 text-muted-foreground">
              Testera is used by educational institutions to assess students of various ages, including minors (persons under 18 years of age). We take special care to protect children's data:
            </p>
            
            <h3 className="text-xl font-medium mb-3 text-foreground">4.1 Additional Safeguards</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Children's data is collected only with verifiable consent from parents, guardians, or authorized educational institutions.</li>
              <li>We collect only the minimum data necessary to provide our assessment services.</li>
              <li>Children's data is subject to enhanced security measures and stricter access controls.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">4.2 Institutional Consent</h3>
            <p className="mb-4 text-muted-foreground">
              When schools or educational institutions use Testera, they act as intermediaries and are responsible for obtaining necessary parental consent. By using our platform through an institution, it is understood that appropriate consent has been obtained.
            </p>

            <h3 className="text-xl font-medium mb-3 text-foreground">4.3 Data Sharing</h3>
            <p className="text-muted-foreground">
              Children's assessment data is shared only with authorized school personnel, teachers, or administrators designated by the institution. We do not share children's data with third parties for marketing purposes.
            </p>
          </section>

          {/* Section 5: Legal Basis */}
          <section id="legal-basis" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">5. Legal Basis Under DPDP Act 2023</h2>
            <p className="mb-4 text-muted-foreground">
              In accordance with the Digital Personal Data Protection Act, 2023, we process your personal data based on the following lawful grounds:
            </p>
            
            <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
              <li><strong>Consent:</strong> You provide explicit consent when creating an account, taking assessments, or enabling camera/microphone access. You may withdraw consent at any time.</li>
              <li><strong>Legitimate Use:</strong> Processing necessary for providing assessment services that you have requested or for which you have registered.</li>
              <li><strong>Purpose Limitation:</strong> We collect data only for specified, explicit, and legitimate purposes and do not process it in ways incompatible with those purposes.</li>
              <li><strong>Data Minimization:</strong> We collect only the data that is necessary for the stated purposes and avoid excessive data collection.</li>
              <li><strong>Notice:</strong> We provide clear notice about what data we collect, why we collect it, and how it will be used before or at the time of collection.</li>
            </ul>
          </section>

          {/* Section 6: Data Storage & Protection */}
          <section id="storage" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">6. Data Storage & Protection</h2>
            
            <h3 className="text-xl font-medium mb-3 text-foreground">6.1 Encryption</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li><strong>In Transit:</strong> All data transmitted between your device and our servers is encrypted using TLS 1.3 (Transport Layer Security).</li>
              <li><strong>At Rest:</strong> Stored data is encrypted using AES-256 encryption standards.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">6.2 Secure Cloud Infrastructure</h3>
            <p className="mb-4 text-muted-foreground">
              We use industry-leading cloud service providers with SOC 2 Type II compliance, ensuring high standards of security, availability, and confidentiality.
            </p>

            <h3 className="text-xl font-medium mb-3 text-foreground">6.3 Access Controls</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Role-based access controls (RBAC) ensure that only authorized personnel can access specific data.</li>
              <li>Regular access reviews and privilege audits are conducted.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">6.4 Monitoring & Auditing</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Comprehensive logging of all data access and modifications.</li>
              <li>Real-time monitoring for suspicious activities.</li>
              <li>Regular security audits and vulnerability assessments.</li>
            </ul>
          </section>

          {/* Section 7: Data Sharing */}
          <section id="sharing" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">7. Data Sharing & Third-Party Disclosures</h2>
            
            <h3 className="text-xl font-medium mb-3 text-foreground">7.1 Schools & Institutions</h3>
            <p className="mb-4 text-muted-foreground">
              Assessment data, scores, and relevant performance information are shared with the educational institution or employer that administers the test. This includes teachers, administrators, or HR personnel as designated by the organization.
            </p>

            <h3 className="text-xl font-medium mb-3 text-foreground">7.2 Service Providers</h3>
            <p className="mb-4 text-muted-foreground">
              We engage trusted third-party service providers for:
            </p>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Cloud hosting and storage</li>
              <li>Email delivery services</li>
              <li>Analytics and monitoring</li>
              <li>AI transcription services (for audio only - see Section 8)</li>
            </ul>
            <p className="mb-4 text-muted-foreground">
              All service providers are bound by data processing agreements that ensure they maintain appropriate security standards and use data only for specified purposes.
            </p>

            <h3 className="text-xl font-medium mb-3 text-foreground">7.3 No Sale of Personal Data</h3>
            <p className="mb-4 text-muted-foreground font-semibold">
              We do not sell, rent, or trade your personal data to any third party for marketing or commercial purposes. Your data is never monetized.
            </p>

            <h3 className="text-xl font-medium mb-3 text-foreground">7.4 Legal Requirements</h3>
            <p className="text-muted-foreground">
              We may disclose your information if required by law, court order, or government authority, or if we believe disclosure is necessary to protect our rights, safety, or the rights and safety of others.
            </p>
          </section>

          {/* Section 8: Video & Media Data Handling - CRITICAL SECTION */}
          <section id="video-media" className="mb-10 scroll-mt-20 p-6 bg-primary/5 rounded-lg border-2 border-primary/20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">8. Video & Media Data Handling</h2>
            
            <div className="bg-card p-4 rounded-lg border border-border mb-6">
              <h3 className="text-lg font-bold mb-2 text-primary">Important Clarification About AI Processing</h3>
              <p className="text-foreground font-medium">
                <strong>Video recordings are NOT sent to any AI system for analysis.</strong> Videos are stored securely for proctoring and integrity verification purposes only. They may be reviewed by authorized personnel (test creators or institutional administrators) but are never processed by artificial intelligence.
              </p>
              <p className="mt-3 text-foreground">
                <strong>Only anonymized audio data</strong> extracted from video responses is transcribed using AI services (OpenAI Whisper) to convert spoken answers into text for evaluation. The audio is processed without any identifying information attached, and the transcription service does not retain the audio after processing.
              </p>
            </div>

            <h3 className="text-xl font-medium mb-3 text-foreground">8.1 Purpose of Video Recording</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>To enable video-based question responses where candidates record spoken or visual answers.</li>
              <li>To capture periodic webcam snapshots for integrity monitoring and identity verification.</li>
              <li>To deter and detect cheating or impersonation during assessments.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">8.2 How Videos Are Handled</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li><strong>Upload:</strong> Videos are uploaded directly to encrypted cloud storage using secure, resumable upload protocols.</li>
              <li><strong>Storage:</strong> All video files are encrypted at rest and stored in secure cloud infrastructure.</li>
              <li><strong>Audio Extraction:</strong> For video responses, the audio track is extracted and sent (without personal identifiers) to transcription services to convert speech to text.</li>
              <li><strong>Evaluation:</strong> The transcribed text is then evaluated by AI to generate scores and feedback. The video itself is not analyzed by AI.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">8.3 Video Retention</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Video recordings are retained for a default period of 90 days after test completion.</li>
              <li>Institutions may request extended retention periods based on their requirements.</li>
              <li>Videos are deleted upon institutional request or as part of platform data management processes.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">8.4 Who Can Access Videos</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>The candidate who recorded the video.</li>
              <li>The test creator (employer or institution).</li>
              <li>Authorized collaborators designated by the test creator.</li>
              <li>Testera support staff (only when necessary for troubleshooting, with strict access controls).</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">8.5 Deletion</h3>
            <p className="text-muted-foreground">
              You may request deletion of your video recordings at any time by contacting us at contact@testera.ai. Deletion requests are processed within 30 days, subject to any legal retention requirements.
            </p>
          </section>

          {/* Section 9: User Rights */}
          <section id="user-rights" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">9. User Rights Under DPDP Act 2023</h2>
            <p className="mb-4 text-muted-foreground">
              As a Data Principal under the DPDP Act, you have the following rights:
            </p>

            <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
              <li><strong>Right to Access:</strong> You can request a summary of your personal data that we hold and information about how it is being processed.</li>
              <li><strong>Right to Correction:</strong> You can request correction of inaccurate or incomplete personal data.</li>
              <li><strong>Right to Erasure:</strong> You can request deletion of your personal data, subject to legal and contractual obligations.</li>
              <li><strong>Right to Withdraw Consent:</strong> You can withdraw your consent for data processing at any time. This will not affect the lawfulness of processing done before withdrawal.</li>
              <li><strong>Right to Grievance Redressal:</strong> You have the right to register complaints and receive resolution within the stipulated timeframe.</li>
              <li><strong>Right to Nominate:</strong> You can nominate another individual to exercise your rights in case of your death or incapacity.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">How to Exercise Your Rights</h3>
            <p className="text-muted-foreground">
              To exercise any of these rights, please contact us at <strong>contact@testera.ai</strong> with the subject line "Data Rights Request". We will verify your identity and respond to your request within 30 days.
            </p>
          </section>

          {/* Section 10: Data Retention Policy */}
          <section id="retention" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">10. Data Retention Policy</h2>
            <p className="mb-4 text-muted-foreground">
              We retain your data only for as long as necessary to fulfill the purposes for which it was collected:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse border border-border text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="border border-border p-3 text-left text-foreground">Data Type</th>
                    <th className="border border-border p-3 text-left text-foreground">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="border border-border p-3">Account Information</td>
                    <td className="border border-border p-3">Duration of account + 1 year after deletion</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Test Responses & Scores</td>
                    <td className="border border-border p-3">2 years or as per institutional agreement</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Video Recordings</td>
                    <td className="border border-border p-3">90 days (configurable by institution)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Audio Transcriptions</td>
                    <td className="border border-border p-3">Same as test responses (2 years)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Webcam Snapshots</td>
                    <td className="border border-border p-3">90 days</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Usage Logs</td>
                    <td className="border border-border p-3">1 year</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Monitoring Events</td>
                    <td className="border border-border p-3">1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-muted-foreground">
              After the retention period expires, data is securely deleted using industry-standard data destruction methods. Anonymized and aggregated data may be retained indefinitely for research and platform improvement purposes.
            </p>
          </section>

          {/* Section 11: Cookies & Tracking */}
          <section id="cookies" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">11. Cookies & Tracking</h2>
            
            <h3 className="text-xl font-medium mb-3 text-foreground">11.1 Types of Cookies We Use</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li><strong>Essential Cookies:</strong> Required for authentication, session management, and security. These cannot be disabled.</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform to improve our services.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">11.2 Purpose</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Maintaining your logged-in session</li>
              <li>Load balancing and server optimization</li>
              <li>Security and fraud prevention</li>
              <li>Understanding usage patterns (anonymized)</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">11.3 Managing Cookies</h3>
            <p className="text-muted-foreground">
              You can control cookies through your browser settings. However, disabling essential cookies may prevent you from using certain features of our platform. Most browsers allow you to block or delete cookies, manage cookie preferences, and view stored cookies.
            </p>
          </section>

          {/* Section 12: Data Transfers */}
          <section id="transfers" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">12. Data Transfers</h2>
            
            <h3 className="text-xl font-medium mb-3 text-foreground">12.1 Primary Data Storage</h3>
            <p className="mb-4 text-muted-foreground">
              Your personal data is primarily stored on servers located in India, in compliance with data localization requirements.
            </p>

            <h3 className="text-xl font-medium mb-3 text-foreground">12.2 International Transfers</h3>
            <p className="mb-4 text-muted-foreground">
              In some cases, data may be transferred to servers outside India for processing (e.g., AI transcription services). When this occurs:
            </p>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>We ensure the receiving country has adequate data protection standards.</li>
              <li>We use standard contractual clauses or other approved transfer mechanisms.</li>
              <li>We implement additional safeguards such as encryption and access controls.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">12.3 Third-Party Processor Agreements</h3>
            <p className="text-muted-foreground">
              All third-party processors handling your data are bound by data processing agreements that require them to maintain confidentiality, implement appropriate security measures, and process data only as instructed by Testera.
            </p>
          </section>

          {/* Section 13: Security Measures */}
          <section id="security" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">13. Security Measures</h2>
            <p className="mb-4 text-muted-foreground">
              We implement comprehensive security measures to protect your data:
            </p>

            <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
              <li><strong>Network Security:</strong> Firewalls, intrusion detection systems, and network segmentation to prevent unauthorized access.</li>
              <li><strong>Application Security:</strong> Secure coding practices, regular code reviews, and vulnerability scanning.</li>
              <li><strong>Rate Limiting:</strong> Protection against brute force attacks and denial-of-service attacks.</li>
              <li><strong>Backups:</strong> Regular encrypted backups with secure off-site storage for disaster recovery.</li>
              <li><strong>Security Reviews:</strong> Periodic security reviews and assessments as part of our continuous improvement process.</li>
              <li><strong>Employee Training:</strong> Regular security awareness training for all staff members.</li>
              <li><strong>Incident Response:</strong> Documented procedures for detecting, responding to, and recovering from security incidents.</li>
            </ul>

            <p className="text-muted-foreground">
              While we implement robust security measures, no system is completely secure. We encourage you to use strong passwords and protect your account credentials.
            </p>
          </section>

          {/* Section 14: Grievance Redressal */}
          <section id="grievance" className="mb-10 scroll-mt-20 p-6 bg-muted/30 rounded-lg border border-border">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">14. Grievance Redressal Mechanism</h2>
            <p className="mb-4 text-muted-foreground">
              In accordance with the DPDP Act 2023, we have appointed a Grievance Officer to address your concerns regarding data privacy:
            </p>

            <div className="bg-card p-4 rounded-lg border border-border mb-4">
              <p className="text-foreground"><strong>Grievance Officer:</strong> Utkarsh Gupta</p>
              <p className="text-foreground"><strong>Email:</strong> contact@testera.ai</p>
              <p className="text-foreground"><strong>Phone:</strong> +91 9717100100</p>
            </div>

            <h3 className="text-xl font-medium mb-3 text-foreground">Response Timeline</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Acknowledgment of complaint: Within 48 hours</li>
              <li>Resolution of complaint: Within 30 days of receipt</li>
              <li>If additional time is required, you will be informed of the reason and expected timeline.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">Escalation</h3>
            <p className="text-muted-foreground">
              If you are not satisfied with our response, you may escalate your complaint to the Data Protection Board of India as established under the DPDP Act 2023.
            </p>
          </section>

          {/* Section 15: Policy Updates */}
          <section id="updates" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">15. Policy Updates</h2>
            
            <h3 className="text-xl font-medium mb-3 text-foreground">15.1 Notification of Changes</h3>
            <p className="mb-4 text-muted-foreground">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes:
            </p>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>We will update the "Last Updated" date at the top of this policy.</li>
              <li>We will notify registered users via email for significant changes.</li>
              <li>We may display a prominent notice on our platform.</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 text-foreground">15.2 Version History</h3>
            <ul className="list-disc pl-6 mb-4 text-muted-foreground">
              <li>Version 2.0 (December 3, 2025): Comprehensive update with DPDP Act 2023 compliance and detailed video/audio handling clarification.</li>
              <li>Version 1.0 (May 1, 2025): Initial privacy policy.</li>
            </ul>

            <p className="text-muted-foreground">
              Your continued use of our services after any changes indicates your acceptance of the updated policy.
            </p>
          </section>

          {/* Section 16: Contact Information */}
          <section id="contact" className="mb-10 scroll-mt-20">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">16. Contact Information</h2>
            <p className="mb-4 text-muted-foreground">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>

            <div className="bg-muted/30 p-6 rounded-lg border border-border">
              <p className="text-foreground mb-2"><strong>Company:</strong> Testera</p>
              <p className="text-foreground mb-2"><strong>Email:</strong> contact@testera.ai</p>
              <p className="text-foreground mb-2"><strong>Phone:</strong> +91 9717100100 | +91 9100167640</p>
              <p className="text-foreground mb-4"><strong>Grievance Officer:</strong> Utkarsh Gupta (contact@testera.ai)</p>
              <p className="text-muted-foreground text-sm">
                We aim to respond to all inquiries within 48 hours during business days.
              </p>
            </div>
          </section>

          <div className="border-t border-border pt-6 mt-8">
            <Link to="/" className="text-secondary hover:text-secondary/90">
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
